import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../Logo';
import { 
  FiMenu, 
  FiX, 
  FiUser, 
  FiLogOut, 
  FiSettings, 
  FiChevronDown,
  FiGrid,
  FiBarChart2
} from 'react-icons/fi';
import styles from './Header.module.css';

const Header = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effects for header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleToggleMobileMenu = () => {
    setMobileMenuOpen(prev => !prev);
    // Close user menu if open
    if (userMenuOpen) setUserMenuOpen(false);
  };

  const handleToggleUserMenu = () => {
    setUserMenuOpen(prev => !prev);
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <Link to="/" className={styles.logoLink}>
            <Logo color={scrolled ? "primary" : "white"} size="medium" />
            <span className={styles.logoText}>QR Butler</span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className={styles.desktopNav}>
          {user ? (
            <>
              <Link 
                to="/dashboard" 
                className={`${styles.navLink} ${isActive('/dashboard') ? styles.active : ''}`}
              >
                <FiGrid size={18} />
                <span>Dashboard</span>
              </Link>
              <Link 
                to="/analytics" 
                className={`${styles.navLink} ${isActive('/analytics') ? styles.active : ''}`}
              >
                <FiBarChart2 size={18} />
                <span>Analytics</span>
              </Link>
            </>
          ) : (
            <>
              <Link to="/features" className={styles.navLink}>Features</Link>
              <Link to="/pricing" className={styles.navLink}>Pricing</Link>
              <Link to="/contact" className={styles.navLink}>Contact</Link>
            </>
          )}
        </nav>

        {/* Auth Buttons or User Menu */}
        <div className={styles.authContainer}>
          {user ? (
            <div className={styles.userMenuContainer}>
              <button 
                className={styles.userMenuButton} 
                onClick={handleToggleUserMenu}
                aria-expanded={userMenuOpen}
                aria-label="User menu"
              >
                <div className={styles.userAvatar}>
                  {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </div>
                <span className={styles.userName}>{user.email?.split('@')[0] || 'User'}</span>
                <FiChevronDown size={16} className={`${styles.chevron} ${userMenuOpen ? styles.rotated : ''}`} />
              </button>
              
              {userMenuOpen && (
                <div className={styles.userMenu}>
                  <div className={styles.userInfo}>
                    <div className={styles.userAvatar}>
                      {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div>
                      <p className={styles.userFullName}>{user.email?.split('@')[0] || 'User'}</p>
                      <p className={styles.userEmail}>{user.email}</p>
                    </div>
                  </div>
                  
                  <div className={styles.menuDivider}></div>
                  
                  <Link to="/dashboard" className={styles.menuItem}>
                    <FiGrid size={16} />
                    <span>Dashboard</span>
                  </Link>
                  <Link to="/account" className={styles.menuItem}>
                    <FiUser size={16} />
                    <span>Account</span>
                  </Link>
                  <Link to="/settings" className={styles.menuItem}>
                    <FiSettings size={16} />
                    <span>Settings</span>
                  </Link>
                  
                  <div className={styles.menuDivider}></div>
                  
                  <button onClick={handleSignOut} className={styles.signOutButton}>
                    <FiLogOut size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.authButtons}>
              <Link to="/login" className={styles.loginButton}>Log In</Link>
              <Link to="/signup" className={styles.signupButton}>Sign Up</Link>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className={styles.mobileMenuButton} 
          onClick={handleToggleMobileMenu}
          aria-expanded={mobileMenuOpen}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <nav className={styles.mobileNav}>
            {user ? (
              <>
                <Link to="/dashboard" className={styles.mobileNavLink}>
                  <FiGrid size={20} />
                  <span>Dashboard</span>
                </Link>
                <Link to="/analytics" className={styles.mobileNavLink}>
                  <FiBarChart2 size={20} />
                  <span>Analytics</span>
                </Link>
                <Link to="/account" className={styles.mobileNavLink}>
                  <FiUser size={20} />
                  <span>Account</span>
                </Link>
                <Link to="/settings" className={styles.mobileNavLink}>
                  <FiSettings size={20} />
                  <span>Settings</span>
                </Link>
                <button onClick={handleSignOut} className={styles.mobileSignOutButton}>
                  <FiLogOut size={20} />
                  <span>Sign Out</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/features" className={styles.mobileNavLink}>Features</Link>
                <Link to="/pricing" className={styles.mobileNavLink}>Pricing</Link>
                <Link to="/contact" className={styles.mobileNavLink}>Contact</Link>
                <Link to="/login" className={styles.mobileNavLink}>Log In</Link>
                <Link to="/signup" className={styles.mobileAuthButton}>Sign Up</Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
